import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  Hammer,
  Wrench,
  Palette,
  Layers,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, NAV_GROUPS, MEGA_FEATURE } from "@/lib/site-data";

const ICONS = { Hammer, Wrench, Palette, Layers };

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null); // mobile accordion
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  const goToQuote = () => {
    if (location.pathname === "/") {
      const el = document.getElementById("ajanlat");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#ajanlat");
    }
  };

  const flatLinks = NAV_GROUPS.flatMap((g) => g.items);
  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
          : "bg-white/85 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <div className="relative shrink-0">
            <img
              src="/assets/logo.png"
              alt="Fortuna Ponyva Kft. logó"
              className="w-11 h-11 md:w-12 md:h-12 rounded-md object-contain shadow-sm ring-1 ring-gray-200 group-hover:ring-[#EA580C]/50 transition-all"
            />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-[#0B192C] text-base md:text-lg">
              Fortuna Ponyva Kft.
            </div>
            <div className="text-[10px] md:text-[11px] text-gray-500 tracking-[0.2em] uppercase">
              Since 2003 · Dunaharaszti
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          <div
            className="relative"
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
          >
            <button
              data-testid="nav-mega-trigger"
              className="text-sm font-medium text-gray-700 hover:text-[#EA580C] transition-colors inline-flex items-center gap-1 h-20"
            >
              Szolgáltatások <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {megaOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-[1080px] pt-2">
                <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden grid grid-cols-12 gap-px bg-gray-200">
                  {NAV_GROUPS.map((g) => {
                    const Icon = ICONS[g.icon] || Layers;
                    return (
                      <div key={g.label} className="bg-white p-5 col-span-3">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-md bg-[#EA580C]/10 text-[#EA580C] flex items-center justify-center">
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="font-heading font-bold text-[#0B192C] text-sm">
                            {g.label}
                          </span>
                        </div>
                        <ul className="space-y-1">
                          {g.items.map((it) => (
                            <li key={it.to}>
                              <Link
                                to={it.to}
                                data-testid={`nav-mega-${it.to.replace("/", "")}`}
                                className={`block px-2 py-1.5 rounded-md text-xs leading-tight hover:bg-[#F8FAFC] transition-colors ${
                                  isActive(it.to)
                                    ? "text-[#EA580C] font-semibold"
                                    : "text-gray-700"
                                }`}
                              >
                                <span className="font-medium">{it.label}</span>
                                <span className="block text-[11px] text-gray-400 mt-0.5">
                                  {it.desc}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {/* Featured CTA card under mega menu */}
                <Link
                  to={MEGA_FEATURE.to}
                  data-testid="nav-mega-feature"
                  className="mt-2 block bg-gradient-to-r from-[#0B192C] via-[#1E3E62] to-[#0B192C] rounded-lg overflow-hidden border border-[#EA580C]/30 group relative shadow-2xl"
                >
                  <div
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 16px)",
                    }}
                  />
                  <div className="relative flex items-center justify-between p-4 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="px-2.5 py-1 rounded-full bg-[#EA580C] text-white text-[10px] uppercase tracking-widest font-bold glow-pulse">
                        {MEGA_FEATURE.badge}
                      </div>
                      <div>
                        <div className="font-heading font-bold text-white text-base">
                          {MEGA_FEATURE.title}
                        </div>
                        <div className="text-xs text-gray-300">
                          {MEGA_FEATURE.desc}
                        </div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 text-[#EA580C] font-semibold text-sm group-hover:gap-3 transition-all">
                      {MEGA_FEATURE.cta}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </div>
            )}
          </div>
          <Link
            to="/akciok"
            data-testid="nav-link-akciok"
            className={`text-sm font-medium transition-colors ${
              isActive("/akciok")
                ? "text-[#EA580C]"
                : "text-gray-700 hover:text-[#EA580C]"
            }`}
          >
            Akciók
          </Link>
          <Link
            to="/rolunk"
            data-testid="nav-link-rolunk"
            className={`text-sm font-medium transition-colors ${
              isActive("/rolunk")
                ? "text-[#EA580C]"
                : "text-gray-700 hover:text-[#EA580C]"
            }`}
          >
            Rólunk
          </Link>
          <Link
            to="/kapcsolat"
            data-testid="nav-link-kapcsolat"
            className={`text-sm font-medium transition-colors ${
              isActive("/kapcsolat")
                ? "text-[#EA580C]"
                : "text-gray-700 hover:text-[#EA580C]"
            }`}
          >
            Kapcsolat
          </Link>
          <Link
            to="/palyazatok"
            data-testid="nav-link-palyazatok"
            className={`text-sm font-medium transition-colors ${
              isActive("/palyazatok")
                ? "text-[#EA580C]"
                : "text-gray-700 hover:text-[#EA580C]"
            }`}
          >
            Pályázatok
          </Link>
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <a
            data-testid="nav-phone-link"
            href={`tel:${COMPANY.phoneRaw}`}
            className="flex items-center gap-2 text-sm font-semibold text-[#1E3E62] hover:text-[#EA580C] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <Button
            data-testid="nav-cta-quote"
            onClick={goToQuote}
            className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold px-5 h-10"
          >
            Ajánlatkérés
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Menü"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden p-2 -mr-2 text-[#1E3E62]"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to={MEGA_FEATURE.to}
              data-testid="nav-mobile-feature"
              className="mb-3 flex items-center justify-between gap-3 p-3 rounded-md bg-gradient-to-r from-[#0B192C] to-[#1E3E62] text-white border border-[#EA580C]/30"
            >
              <div className="flex items-center gap-3">
                <span className="px-2 py-0.5 rounded-full bg-[#EA580C] text-[10px] uppercase tracking-widest font-bold">
                  {MEGA_FEATURE.badge}
                </span>
                <span className="font-heading font-bold text-sm">{MEGA_FEATURE.title}</span>
              </div>
              <ArrowRight className="w-4 h-4 text-[#EA580C]" />
            </Link>
            {NAV_GROUPS.map((g) => {
              const Icon = ICONS[g.icon] || Layers;
              const isOpenGroup = openGroup === g.label;
              return (
                <div key={g.label} className="border-b border-gray-100 last:border-0">
                  <button
                    onClick={() =>
                      setOpenGroup(isOpenGroup ? null : g.label)
                    }
                    className="w-full flex items-center justify-between py-3 text-[#0B192C] font-heading font-bold"
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-[#EA580C]" />
                      {g.label}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        isOpenGroup ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpenGroup && (
                    <div className="pb-3 pl-7 flex flex-col gap-1">
                      {g.items.map((it) => (
                        <Link
                          key={it.to}
                          to={it.to}
                          className={`px-2 py-2 rounded text-sm ${
                            isActive(it.to)
                              ? "text-[#EA580C] font-semibold"
                              : "text-gray-700 hover:text-[#EA580C]"
                          }`}
                        >
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/akciok"
              className="px-2 py-3 font-heading font-bold text-[#0B192C] border-b border-gray-100"
            >
              Akciók
            </Link>
            <Link
              to="/rolunk"
              className="px-2 py-3 font-heading font-bold text-[#0B192C] border-b border-gray-100"
            >
              Rólunk
            </Link>
            <Link
              to="/kapcsolat"
              className="px-2 py-3 font-heading font-bold text-[#0B192C] border-b border-gray-100"
            >
              Kapcsolat
            </Link>
            <Link
              to="/palyazatok"
              className="px-2 py-3 font-heading font-bold text-[#0B192C] border-b border-gray-100"
            >
              Pályázatok
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="flex items-center gap-2 px-2 py-3 mt-2 text-[#1E3E62] font-semibold"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <Button
              data-testid="nav-mobile-cta"
              onClick={goToQuote}
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold mt-2"
            >
              Ajánlatkérés <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
